import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import YouTubeWalkthroughEmbed from './YouTubeWalkthroughEmbed';
import {
  COGNIDOOR_YOUTUBE_CHANNEL,
  type WalkthroughVideo,
} from '../../config/walkthroughVideos';

type WalkthroughCarouselProps = {
  videos: WalkthroughVideo[];
  showChannelLink?: boolean;
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 48 : -48,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -48 : 48,
    opacity: 0,
  }),
};

export default function WalkthroughCarousel({
  videos,
  showChannelLink = true,
}: WalkthroughCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const count = videos.length;
  const activeVideo = videos[activeIndex];

  const goTo = useCallback(
    (index: number) => {
      if (index === activeIndex || count === 0) return;
      const wrapped = ((index % count) + count) % count;
      setDirection(wrapped > activeIndex ? 1 : -1);
      if (wrapped === 0 && activeIndex === count - 1) setDirection(1);
      if (wrapped === count - 1 && activeIndex === 0) setDirection(-1);
      setActiveIndex(wrapped);
    },
    [activeIndex, count]
  );

  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  useEffect(() => {
    if (activeIndex >= count) {
      setActiveIndex(0);
    }
  }, [activeIndex, count]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  if (!activeVideo) return null;

  return (
    <div className="mx-auto w-full max-w-5xl" tabIndex={0}>
      <div className="mb-5 flex gap-2 overflow-x-auto pb-1 sm:justify-center sm:overflow-visible">
        {videos.map((video, index) => (
          <button
            key={video.key}
            type="button"
            onClick={() => goTo(index)}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-300 ${
              index === activeIndex
                ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-md'
                : 'border border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:bg-sky-50/80 hover:text-sky-800'
            }`}
            aria-pressed={index === activeIndex}
          >
            {video.pillLabel}
          </button>
        ))}
      </div>

      <div className="relative rounded-3xl border border-slate-200/80 bg-white/95 p-4 shadow-lg shadow-slate-200/40 sm:p-6">
        {count > 1 ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 top-[42%] z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-md transition hover:border-sky-200 hover:text-sky-700 sm:left-4"
              aria-label="Previous video"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 top-[42%] z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-md transition hover:border-sky-200 hover:text-sky-700 sm:right-4"
              aria-label="Next video"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        ) : null}

        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={activeVideo.key}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            >
              <YouTubeWalkthroughEmbed video={activeVideo} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-left">
            <h3 className="text-base font-semibold text-slate-900 sm:text-lg">{activeVideo.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{activeVideo.description}</p>
          </div>
          <Link
            to={activeVideo.productPath}
            className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg sm:self-center"
          >
            Open {activeVideo.productLabel}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        {count > 1 ? (
          <div className="mt-4 flex justify-center gap-2">
            {videos.map((video, index) => (
              <button
                key={video.key}
                type="button"
                onClick={() => goTo(index)}
                className={`h-2 rounded-full transition-all ${
                  index === activeIndex ? 'w-6 bg-sky-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to ${video.pillLabel}`}
              />
            ))}
          </div>
        ) : null}
      </div>

      {showChannelLink ? (
        <p className="mt-6 text-center text-sm text-slate-600">
          <a
            href={COGNIDOOR_YOUTUBE_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-semibold text-sky-700 underline-offset-2 hover:text-sky-800 hover:underline"
          >
            More on YouTube
          </a>
        </p>
      ) : null}
    </div>
  );
}
